import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RefreshCw, LogOut, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const KasirWhatsApp = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  const isConnected = useMemo(() => Boolean(status?.ready), [status]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (!token || !userStr) {
      navigate('/kasir/login');
      return;
    }

    try {
      const user = JSON.parse(userStr);
      if (!['kasir', 'admin'].includes(user.role)) {
        navigate('/kasir/login');
        return;
      }
    } catch (error) {
      navigate('/kasir/login');
      return;
    }

    const load = async () => {
      try {
        const statusResp = await api.get('/kasir/whatsapp/status');
        setStatus(statusResp.data);
        if (!statusResp.data?.ready) {
          const qrResp = await api.get('/kasir/whatsapp/qr');
          setQr(qrResp.data?.qr || null);
        } else {
          setQr(null);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Gagal memuat status WhatsApp');
      } finally {
        setLoading(false);
      }
    };

    load();
    timerRef.current = setInterval(load, 2500);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [navigate]);

  const handleRefresh = async () => {
    setBusy(true);
    try {
      const statusResp = await api.get('/kasir/whatsapp/status');
      setStatus(statusResp.data);
      if (!statusResp.data?.ready) {
        const qrResp = await api.get('/kasir/whatsapp/qr');
        setQr(qrResp.data?.qr || null);
      } else {
        setQr(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal refresh status WhatsApp');
    } finally {
      setBusy(false);
    }
  };

  const handleLogoutDevice = async () => {
    setBusy(true);
    try {
      await api.post('/kasir/whatsapp/logout');
      toast.success('WhatsApp direset. Silakan scan ulang.');
      await handleRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal reset WhatsApp');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-secondary rounded-md flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-heading font-black italic text-secondary uppercase">WhatsApp</span>
            </div>
            <Link
              to="/kasir/dashboard"
              className="text-zinc-400 hover:text-secondary font-medium transition-colors"
            >
              Kembali
            </Link>
          </div>
        </div>
      </nav>

      <div className="mx-auto w-full max-w-screen-xl px-4 sm:px-6 lg:px-10 py-8 lg:py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
              <div>
                <div className="text-white font-bold text-xl">Scan QR WhatsApp</div>
                <div className="text-zinc-400 text-sm">
                  Untuk menerima notifikasi booking baru dan kebutuhan kasir.
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={busy}
                  className="bg-zinc-800 border border-zinc-700 text-white hover:border-secondary hover:text-secondary rounded-lg px-4 py-2 font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={handleLogoutDevice}
                  disabled={busy}
                  className="bg-red-500/10 border border-red-500/20 text-red-300 hover:border-red-500/40 rounded-lg px-4 py-2 font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <LogOut className="w-4 h-4" />
                  Reset
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block w-12 h-12 border-4 border-secondary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-6">
                  <div className="text-sm text-zinc-400 font-semibold uppercase mb-2">Status</div>
                  <div className="text-white font-bold text-2xl">
                    {isConnected ? 'TERHUBUNG' : 'BELUM TERHUBUNG'}
                  </div>
                  <div className="text-zinc-400 mt-2">
                    State: <span className="text-white font-semibold">{String(status?.state || '-')}</span>
                  </div>
                </div>

                <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-6">
                  <div className="text-sm text-zinc-400 font-semibold uppercase mb-2">QR Code</div>
                  {isConnected ? (
                    <div className="text-primary font-bold">Sudah login. Tidak perlu scan QR.</div>
                  ) : qr ? (
                    <div className="flex items-center justify-center bg-white rounded-lg p-4">
                      <img src={qr} alt="QR WhatsApp" className="max-w-full h-auto" />
                    </div>
                  ) : (
                    <div className="text-zinc-400">QR belum tersedia. Coba refresh.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default KasirWhatsApp;
